import type { EntityDetail, TenantMembership } from '@cdf/api-client-react';
import {
  BoxesIcon,
  Building2Icon,
  KeyRoundIcon,
  ScrollTextIcon,
  Settings2Icon,
  SparklesIcon,
  UserPlusIcon,
  UsersRoundIcon
} from 'lucide-react';

import { hasTenantPermission } from '@/features/tenancy/permissions';

export const tenantAdministrationNavigation = [
  { to: 'tenant/entity', label: 'Overview', description: 'Entity status, profile and administration shortcuts.', keywords: ['entity', 'profile', 'status'], icon: Building2Icon, permissions: ['tenant.settings.read'] },
  { to: 'tenant/members', label: 'Members', description: 'People with explicit access to this entity.', keywords: ['people', 'grants', 'access'], icon: UsersRoundIcon, permissions: ['tenant.members.read'] },
  { to: 'tenant/invites', label: 'Invitations', description: 'Invite people to this entity.', keywords: ['invites', 'people'], icon: UserPlusIcon, permissions: ['tenant.invites.read'] },
  { to: 'tenant/roles', label: 'Roles', description: 'Review and manage entity roles.', keywords: ['permissions', 'access'], icon: KeyRoundIcon, permissions: ['tenant.roles.read'] },
  { to: 'tenant/modules', label: 'Modules', description: 'Feature controls for this entity and its parent.', keywords: ['features', 'controls'], icon: BoxesIcon, permissions: ['tenant.modules.manage'] },
  { to: 'tenant/settings', label: 'Settings', description: 'Structured entity configuration.', keywords: ['configuration', 'json'], icon: Settings2Icon, permissions: ['tenant.settings.read'] },
  { to: 'tenant/audit', label: 'Audit history', description: 'Security and administration activity.', keywords: ['events', 'history'], icon: ScrollTextIcon, permissions: ['tenant.audit.read'] },
  { to: 'tenant/elevation', label: 'Access elevation', description: 'Request or approve temporary access.', keywords: ['temporary', 'approval'], icon: SparklesIcon, permissions: ['tenant.elevation.request', 'tenant.elevation.approve'] }
] as const;

export const entityAdministrationSections = [
  { to: 'tenant/entity#profile', label: 'Profile', description: 'Name, contact details and advanced profile fields.', keywords: ['identity', 'website', 'industry', 'locations'], permissions: ['tenant.settings.read'] },
  { to: 'tenant/entity#customer-settings', label: 'Branding and privacy', description: 'Customer defaults and permitted entity overrides.', keywords: ['logo', 'theme', 'audience', 'booking', 'desk'], permissions: ['tenant.settings.read'] },
  { to: 'tenant/entity#granted-entities', label: 'Granted entities', description: 'Browse entities you can access.', keywords: ['entities', 'switch'], permissions: ['tenant.settings.read'] },
  { to: 'tenant/entity#lifecycle', label: 'Entity lifecycle', description: 'Suspend or restore this entity with an audited reason.', keywords: ['suspend', 'restore'], permissions: ['tenant.settings.manage'] }
] as const;

export function canOpenTenantPage(
  grantedPermissions: readonly string[],
  requiredPermissions: readonly string[]
): boolean {
  return requiredPermissions.some((permission) => hasTenantPermission(grantedPermissions, permission));
}

export type AdministrationDestination = {
  to: string;
  label: string;
  description: string;
  keywords: readonly string[];
};

export function authorizedAdministrationDestinations(
  permissions: readonly string[],
  options: { active: boolean; includeSections?: boolean; availableSections?: readonly string[] }
): AdministrationDestination[] {
  const routes = tenantAdministrationNavigation
    .filter((item) => options.active || item.to === 'tenant/entity')
    .filter((item) => canOpenTenantPage(permissions, item.permissions));
  const sections = options.includeSections
    ? entityAdministrationSections
      .filter((item) => options.availableSections?.includes(item.to) ?? true)
      .filter((item) => canOpenTenantPage(permissions, item.permissions))
    : [];
  return [...routes, ...sections];
}

export function availableEntitySections(entity: Pick<EntityDetail, 'status' | 'parentStatus'> | undefined, active: boolean): string[] {
  if (!entity) return [];
  const sections = ['tenant/entity#profile'];
  if (active) sections.push('tenant/entity#granted-entities');
  if (entity.status === 'active' && entity.parentStatus === 'active') sections.push('tenant/entity#customer-settings');
  if (entity.status !== 'archived' && entity.parentStatus === 'active') sections.push('tenant/entity#lifecycle');
  return sections;
}

export function searchAdministrationDestinations(
  destinations: readonly AdministrationDestination[],
  query: string
): AdministrationDestination[] {
  const words = query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [...destinations];
  return destinations.filter((item) => {
    const haystack = [item.label, item.description, ...item.keywords].join(' ').toLocaleLowerCase();
    return words.every((word) => haystack.includes(word));
  });
}

export function firstTenantAdministrationPage(permissions: readonly string[]): string | undefined {
  return tenantAdministrationNavigation.find(
    (item) => item.to !== 'tenant/entity' && canOpenTenantPage(permissions, item.permissions)
  )?.to;
}

export function tenantAdministrationLanding(
  entity: Pick<TenantMembership, 'status' | 'permissions'>
): string | undefined {
  if (entity.status !== 'active') return undefined;
  return hasTenantPermission(entity.permissions, 'tenant.settings.read')
    ? 'tenant/entity'
    : firstTenantAdministrationPage(entity.permissions);
}
